/**
 * The review layer — the second artifact of the two-artifact split.
 *
 * Mirrors backend/oio_service/schemas/review_layer.py::DraftReviewReport.
 * Everything the system wants the officer to KNOW about the draft (validity /
 * threshold findings, citation verification states, gate results) lives here
 * and never renders into the signed order text.
 */

export interface CitationReview {
  allegation_id: string
  citation: string
  case_name: string
  verification_status: string // verified | mismatch | unresolved | unverified | officer_confirmed | discarded
  confidence: number
  reason: string
  resolved_source: string
  doc_id: string
  source_url: string
  matched_case_name: string
  court: string
  year: number | null
  officer_action: string
  officer_id: string
  officer_email: string
  officer_action_at: string
}

export interface ThresholdFindingView {
  kind: string
  status: string // live | suppressed
  suppression_reason: string
  headline: string
  facts: string[]
  basis: string
}

/** One finding of the deterministic gate (backend nodes/compile_oio/gate.py::GateDefect). */
export interface GateDefectView {
  code: string
  severity: "fatal" | "error" | "warning"
  /** "" = about the order as a whole. */
  section_number: string
  message: string
}

/** One row of the positive checklist (backend schemas/requirements_ledger.py). */
export interface RequirementEntryView {
  requirement_id: string
  title: string
  status: string // satisfied | defective | not_applicable
  defect_codes: string[]
}

/** One finding of the appellate-eye reviewer (backend nodes/compile_oio/adversarial.py). */
export interface AdversarialFindingView {
  section_number: string
  severity: string // concern | observation
  claim: string
  basis: string
}

export interface DraftReviewReport {
  schema_version: number
  thread_id: string
  generated_at: string
  threshold_findings: ThresholdFindingView[]
  validity_warnings: string[]
  citation_reviews: CitationReview[]
  unverified_citation_count: number
  gate_report: Record<string, unknown> | null
  /**
   * The gate's findings, the checklist that names them in words, and the
   * reviewer's concerns — what the Next steps tab is built from
   * (lib/next-steps.ts). They were computed and shipped on every draft and
   * DROPPED here on parse, so no screen ever showed an officer why an order
   * was not right.
   */
  gate_defects: GateDefectView[]
  requirements: RequirementEntryView[]
  reviewer_findings: AdversarialFindingView[]
  coverage_notes: string[]
  contention_total: number
  contention_resolved: number
}

/** Statuses that need officer attention (chips). Mirrors review.py. */
export const UNVERIFIED_STATUSES = new Set(["mismatch", "unresolved", "unverified"])

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value.filter((v) => v && typeof v === "object") as T[]) : []
}

export function parseReviewReport(json: string | null | undefined): DraftReviewReport | null {
  if (!json) return null
  try {
    const parsed = JSON.parse(json) as Partial<DraftReviewReport>
    if (!parsed || typeof parsed !== "object") return null
    const raw = parsed as Record<string, unknown>
    return {
      schema_version: parsed.schema_version ?? 1,
      thread_id: parsed.thread_id ?? "",
      generated_at: parsed.generated_at ?? "",
      threshold_findings: Array.isArray(parsed.threshold_findings)
        ? parsed.threshold_findings
        : [],
      validity_warnings: Array.isArray(parsed.validity_warnings) ? parsed.validity_warnings : [],
      citation_reviews: Array.isArray(parsed.citation_reviews) ? parsed.citation_reviews : [],
      unverified_citation_count: parsed.unverified_citation_count ?? 0,
      gate_report: (parsed.gate_report as Record<string, unknown> | null) ?? null,
      gate_defects: listOf<GateDefectView>((parsed.gate_report as { defects?: unknown } | null)?.defects),
      requirements: listOf<RequirementEntryView>(
        (raw.requirements_ledger as { requirements?: unknown } | null | undefined)?.requirements,
      ),
      reviewer_findings: listOf<AdversarialFindingView>(
        (raw.adversarial_review as { findings?: unknown } | null | undefined)?.findings,
      ),
      coverage_notes: Array.isArray(parsed.coverage_notes) ? parsed.coverage_notes : [],
      contention_total: parsed.contention_total ?? 0,
      contention_resolved: parsed.contention_resolved ?? 0,
    }
  } catch {
    return null
  }
}

export function unverifiedCitationsFor(
  report: DraftReviewReport | null,
  allegationId: string,
): CitationReview[] {
  if (!report) return []
  return report.citation_reviews.filter(
    (c) => c.allegation_id === allegationId && UNVERIFIED_STATUSES.has(c.verification_status),
  )
}

/**
 * Curated standard authorities (the doctrine pack) applied by the pipeline for
 * this allegation — pre-verified constants the officer's own reasoning relies
 * on (e.g. the Section 74 suppression standard), as opposed to citations the
 * noticee raised. Discarded ones drop off the badge.
 */
export function curatedDoctrineFor(
  report: DraftReviewReport | null,
  allegationId: string,
): CitationReview[] {
  if (!report) return []
  return report.citation_reviews.filter(
    (c) =>
      c.allegation_id === allegationId &&
      c.resolved_source === "curated_doctrine" &&
      c.verification_status !== "discarded" &&
      c.officer_action !== "discarded",
  )
}
